'use client';

import { useTransition, type ComponentType, type SVGProps } from 'react';
import { useLocale, useTranslations } from 'next-intl';
import { useRouter } from 'next/navigation';
import { setLocaleAction } from '@/app/actions/set-locale';
import { routing, type Locale } from '@/i18n/routing';
import { FrenchFlag } from '@/components/ui/icons/french-flag';
import { UnitedKingdomFlag } from '@/components/ui/icons/united-kingdom-flag';
import { cn } from '@/lib/utils/cn';

const flagFor: Record<Locale, ComponentType<SVGProps<SVGSVGElement>>> = {
  fr: FrenchFlag,
  en: UnitedKingdomFlag,
};

function localeName(t: ReturnType<typeof useTranslations<'locale'>>, locale: Locale): string {
  switch (locale) {
    case 'fr':
      return t('names.fr');
    case 'en':
      return t('names.en');
  }
}

type Props = {
  className?: string;
};

export function LocaleSwitcher({ className }: Props) {
  const t = useTranslations('locale');
  const current = useLocale() as Locale;
  const router = useRouter();
  const [pending, startTransition] = useTransition();

  const change = (locale: Locale) => {
    if (locale === current) return;
    startTransition(async () => {
      await setLocaleAction(locale);
      router.refresh();
    });
  };

  const others = routing.locales.filter((l) => l !== current);

  return (
    <div
      role="group"
      aria-label={t('switcher.label')}
      className={cn('group/locale relative', className)}
    >
      <FlagCard locale={current} />
      {others.length > 0 ? (
        <div
          className={cn(
            'absolute top-full left-0 mt-1.5 flex flex-col gap-1.5 transition-[margin-top,gap] duration-150 ease-out',
            'group-hover/locale:mt-0.5 group-hover/locale:gap-0.5',
            'group-focus-within/locale:mt-0.5 group-focus-within/locale:gap-0.5',
          )}
        >
          {others.map((locale) => (
            <button
              key={locale}
              type="button"
              onClick={() => change(locale)}
              disabled={pending}
              aria-label={localeName(t, locale)}
              className={cn(
                'pointer-events-none translate-y-[-4px] opacity-0 transition-all duration-150 ease-out',
                'group-hover/locale:pointer-events-auto group-hover/locale:translate-y-0 group-hover/locale:opacity-100',
                'focus-visible:pointer-events-auto focus-visible:translate-y-0 focus-visible:opacity-100',
                'rounded-sm focus-visible:ring-2 focus-visible:ring-purple-500/40 focus-visible:outline-none',
                pending && 'cursor-progress',
              )}
            >
              <FlagCard locale={locale} />
            </button>
          ))}
        </div>
      ) : null}
    </div>
  );
}

type FlagCardProps = {
  locale: Locale;
};

function FlagCard({ locale }: FlagCardProps) {
  const Flag = flagFor[locale];
  return (
    <span className="bg-bg-surface hover:bg-bg-subtle inline-flex border border-[rgb(199,199,199)] p-1 transition-colors duration-150">
      <Flag className="block w-4" />
    </span>
  );
}
